import React, { Fragment, useState, useEffect, useContext } from 'react';
import PropertiesCheckboxes from './PropertiesCheckboxes';
import TagGroupsCheckboxes from './TagGroupsCheckboxes';
import EnabledTagGroupCheckboxes from './EnabledTagGroupsCheckboxes';
import { get, endpoint } from '../../../utils/shared/api';
import { FormikHandlersContext } from '../forms/dynamic_forms/DynamicFormikForm';
import { handleError } from '../../utils/errors';

function PropertiesAndTagsCheckboxes(props) {
  const propertiesSubtext =
    'You can include Docalytics documents and published content from across your various websites';

  const tagsSubtext =
    ' Published content will appear if it is tagged with at least one of the tags you select below.';

  const enabledTagGroupIdsSubtext =
    'Choose which tag groups to show \n When content appears in the portal, users will see the tags associated with each piece of content. Users can filter down the list of content using these tags. Choose which tag groups you want to appear alongside your content.  Some teams have tag groups that are used for production purposes and other tag groups that are used for enablement and distribution purposes. This section will not affect which content appears in the portal; it will only affect the tags that users can see.';

  const { values, touched, errors, accountId, id, fetchingComplete } = props;

  const { fetchingApi } = useContext(FormikHandlersContext);
  const [properties, setProperties] = useState([]);
  const [tagGroups, setTagGroups] = useState([]);
  const [visibleTagGroups, setVisibleTagGroups] = useState([]);
  const [toggleableTagGroups, setToggleableTagGroups] = useState([]);

  const fetchData = () => {
    return Promise.all([
      get(endpoint('/api/v1/properties', { accountId })),
      get(endpoint(`/api/v1/tags`, { resourceType: 'account', resourceId: accountId }))
    ])
      .then(([propertyData, tagGroupsData]) => {
        setProperties(
          propertyData.map(property => {
            return {
              value: property.id,
              name: property.name,
              brandProfileName: property.brandProfileName,
              type: property.siteType,
              siteUrl: property.siteUrl,
              bpIds: property.brandProfileIds
            };
          })
        );

        setTagGroups(
          tagGroupsData.map(tagGroup => {
            return {
              label: tagGroup.name,
              value: tagGroup.id,
              tags: tagGroup.tags.map(tag => {
                return { value: tag.id, label: tag.name, bpIds: tag.brandProfileIds };
              }),
              bpIds: tagGroup.brandProfileIds
            };
          })
        );
        fetchingApi.onFetchingComplete(id);
      })
      .catch(error => {
        fetchingApi.onFetchingError(id);
        handleError(error);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const propertyIds = values.get('contentCollectionAttributes.propertyIds', []);

  // When the propertyIds change, show tagGroups and tags
  useEffect(() => {
    const propertyBpIds = [
      ...new Set(properties.filter(p => propertyIds.includes(p.value)).flatMap(p => p.bpIds))
    ];

    const visibleTGs = tagGroups
      .filter(tG => tG.bpIds.filter(bpId => propertyBpIds.includes(bpId)).length)
      .map(tG => {
        tG.tags = tG.tags.filter(t => t.bpIds.filter(bpId => propertyBpIds.includes(bpId)).length);
        return tG;
      });

    setVisibleTagGroups(visibleTGs);
  }, [propertyIds, properties, tagGroups]);

  const tagIds = values.get('contentCollectionAttributes.tagIds', []);

  // When the tagIds change, show tagGroups that can be hidden in the hub
  useEffect(() => {
    const visible = visibleTagGroups.filter(tG => tG.tags.some(tag => tagIds.includes(tag.value)));
    setToggleableTagGroups(visible);
  }, [tagIds, visibleTagGroups]);

  return (
    <Fragment>
      <PropertiesCheckboxes
        {...props}
        id="contentCollectionAttributes.propertyIds"
        label="Choose which content sources to include"
        value={propertyIds}
        error={errors.get('contentCollectionAttributes.propertyIds')}
        touched={touched.get('contentCollectionAttributes.propertyIds')}
        properties={properties}
        renderSubtext={propertiesSubtext}
      />
      {visibleTagGroups.length > 0 && (
        <TagGroupsCheckboxes
          {...props}
          id="contentCollectionAttributes.tagIds"
          label="Choose which tags to include"
          value={tagIds}
          error={errors.get('contentCollectionAttributes.tagIds')}
          touched={touched.get('contentCollectionAttributes.tagIds')}
          tagGroups={visibleTagGroups}
          renderSubtext={tagsSubtext}
        />
      )}
      {toggleableTagGroups.length > 0 && (
        <EnabledTagGroupCheckboxes
          {...props}
          id="contentCollectionAttributes.enabledTagGroupIds"
          label="Choose which tag groups to show"
          value={values.get('contentCollectionAttributes.enabledTagGroupIds', [])}
          error={errors.get('contentCollectionAttributes.enabledTagGroupIds')}
          touched={touched.get('contentCollectionAttributes.enabledTagGroupIds')}
          tagGroups={toggleableTagGroups}
          renderSubtext={enabledTagGroupIdsSubtext}
        />
      )}
    </Fragment>
  );
}

export default PropertiesAndTagsCheckboxes;



// WEBPACK FOOTER //
// ./app/javascript/app/components/integrations/PropertiesAndTagsCheckboxes.jsx